import React, { useEffect, useState } from "react";
import Header from "../../components/Header";
import { AddCircleOutline } from "@mui/icons-material";
import AddLandingPageModal from "../../models/AddLandingPageModal";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import { Box, Button, useTheme } from "@mui/material";
import { useSelector } from "react-redux";
import { selectSponserID } from "../../features/auth/authSlice";
import {
  useGetLandingPagesMutation,
  useRemoveLandingPageMutation,
  useUpdateLandingPageStatusMutation,
} from "../../features/auth/apiSlice";
import { Link } from "react-router-dom";

const Index = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [data, setData] = useState([] as any);
  const sponserId = useSelector(selectSponserID);
  const [getLandingPageAPI] = useGetLandingPagesMutation();
  const [updateStatus] = useUpdateLandingPageStatusMutation();
  const [removeLandingPageAPI] = useRemoveLandingPageMutation();

  useEffect(() => {
    const getLandingPages = async () => {
      const response: any = await getLandingPageAPI({ sponserId }).unwrap();
      if (response.status === 200) {
        setData(response.landingPages);
      }
    };
    getLandingPages();
  }, [getLandingPageAPI, sponserId]);
  const columns = [
    { field: "_id", headerName: "ID", width: 200 },
    {
      field: "title",
      headerName: "Title",
      width: 200,
    },
    {
      field: "description",
      headerName: "Description",
      width: 300,
    },
    {
      field: "url",
      headerName: "URL",
      width: 250,
      renderCell: function A({ row }: { row: any }) {
        const [copied, setCopied] = useState(false);
        return (
          <>
            {copied ? (
              <div>
                <span className="text-green-400 select-none cursor-pointer">
                  Copied!
                </span>
              </div>
            ) : (
              <span
                onClick={(e) => {
                  navigator.clipboard.writeText(e.currentTarget.innerText);
                  setCopied(true);
                  setTimeout(() => {
                    setCopied(false);
                  }, 5000);
                }}
                className="hover:cursor-pointer"
              >
                {window.location.origin + row.url}
              </span>
            )}
          </>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 100,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      renderCell: function A({ row }: { row: any }) {
        const [active, setActive] = useState(row.status === "active");
        const activePressed = async () => {
          setActive(true);
          const response: any = await updateStatus({
            sponserId,
            status: "active",
            landingPageId: row._id,
          }).unwrap();
          if (response.status === 200) {
            setData(response.landingPages);
          }
        };
        const disactivePresses = async () => {
          setActive(false);
          const response: any = await updateStatus({
            sponserId,
            status: "removed",
            landingPageId: row._id,
          }).unwrap();
          if (response.status === 200) {
            setData(response.landingPages);
          }
        };

        const handleRemoveClicked = async () => {
          const response: any = await removeLandingPageAPI({
            sponserId,
            landingPageId: row._id,
          }).unwrap();
          if (response.status === 200) {
            setData(response.landingPages);
          }
        };

        return (
          <div>
            {!active ? (
              <Button
                variant="contained"
                onClick={activePressed}
                style={{ marginRight: "12px" }}
              >
                Active
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={disactivePresses}
                style={{ marginRight: "12px" }}
              >
                Disactive
              </Button>
            )}
            <Button
              variant="contained"
              style={{ backgroundColor: "red" }}
              onClick={handleRemoveClicked}
            >
              Remove
            </Button>
          </div>
        );
      },
    },
  ];

  const [showAddLandingPageModal, setShowAddLandingPageModal] = useState(false);
  const AddComponent = () => {
    return (
      <div
        onClick={() => setShowAddLandingPageModal(true)}
        className={`
            h-[140px] w-[200px]
            flex justify-center items-center
            border border-blue-400 border-dashed rounded
            hover:cursor-pointer
      `}
      >
        <AddCircleOutline fontSize={"large"} />
      </div>
    );
  };

  const LandingPages = () => {
    return (
      <div className="ml-3 p-2 flex items-start w-[100%] h-[140px] border border-blue-400 rounded">
        {data?.map((page: any) => {
          return (
            <LandingPage title={page?.title} key={page?._id} id={page?._id} />
          );
        })}
      </div>
    );
  };

  const LandingPage = ({ title, id }: { title: string; id: string }) => {
    return (
      <Link to={"/products"} state={{ title, id: id }}>
        <div className="flex flex-col items-center hover:cursor-pointer mr-2">
          <div
            className={`
          h-[100px] w-[100px]
          border border-black rounded
          `}
          ></div>
          <span>{title}</span>
        </div>
      </Link>
    );
  };

  return (
    <div className="mt-[100px] ml-5">
      <Header title="Landing Pages" subtitle="Add Landing Page" />
      <div className="flex items-start mt-5">
        <AddComponent />
        <LandingPages />
      </div>
      {showAddLandingPageModal && (
        <AddLandingPageModal
          setShowAddLandingPageModal={setShowAddLandingPageModal}
          setData={setData}
        />
      )}
      <Box
        m={"40px 0 0 0"}
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .name-column--cell": {
            color: colors.greenAccent[300],
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "noone",
            backgroundColor: colors.blueAccent[700],
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.grey[100]} !important`,
          },
        }}
      >
        <DataGrid
          rows={data}
          /*@ts-ignore */
          columns={columns}
          autoHeight={true}
          components={{ Toolbar: GridToolbar }}
          getRowId={(row) => row._id}
        />
      </Box>
    </div>
  );
};

export default Index;
